import { Star, CheckCircle2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useLocation } from "@/lib/locations";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";
import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const REGION_REVIEWS: Record<string, Array<{name: string, location: string, text: string, initials: string}>> = {
  "sacramento-area": [
    { name: "Mindi M.", location: "Sacramento, CA", text: "I had a great experience with Uplevel Cleaning. The service was prompt and the quality of the work was excellent. My house has never looked better!", initials: "MM" },
    { name: "Roberto N.", location: "Roseville, CA", text: "A friend recommended I try Uplevel Cleaning and I was super impressed. They handled the deep clean of my new home perfectly. Highly recommend!", initials: "RN" },
    { name: "Don J. B.", location: "Elk Grove, CA", text: "In a word, perfect. The Uplevel team arrived on time and ready to go. They were quick, thorough, and very professional. Best in Sacramento!", initials: "DJ" },
    { name: "Sarah L.", location: "Folsom, CA", text: "Uplevel Cleaning is a lifesaver. With two kids and a dog, my house gets messy fast. They leave it spotless every single time.", initials: "SL" },
    { name: "Michael T.", location: "Carmichael, CA", text: "Very professional and detail-oriented. The deep clean was incredibly thorough. Will definitely be setting up recurring services.", initials: "MT" },
    { name: "Jessica R.", location: "Davis, CA", text: "Booking was so easy, and the team was super friendly. I love that they use eco-friendly products. Highly recommended!", initials: "JR" }
  ],
  "contra-costa": [
    { name: "Emily R.", location: "Walnut Creek, CA", text: "They did an amazing job on our Walnut Creek home. The attention to detail was fantastic. We will definitely use them again.", initials: "ER" },
    { name: "James K.", location: "Concord, CA", text: "Best cleaning service in Contra Costa. They showed up on time and left everything sparkling clean. Very professional.", initials: "JK" },
    { name: "Linda P.", location: "San Ramon, CA", text: "I've used them for bi-weekly cleaning and they never disappoint. Always reliable, thorough, and friendly.", initials: "LP" },
    { name: "Mark S.", location: "Danville, CA", text: "Great experience! They handled our move-out clean perfectly. The landlord was very impressed.", initials: "MS" },
    { name: "Susan T.", location: "Richmond, CA", text: "I highly recommend Uplevel Cleaning. They transformed my messy house into a spotless sanctuary.", initials: "ST" },
    { name: "David L.", location: "Antioch, CA", text: "Very efficient and detail-oriented. The deep clean exceeded my expectations.", initials: "DL" }
  ],
  "solano": [
    { name: "Sarah B.", location: "Vallejo, CA", text: "Incredible service! Our house in Vallejo has never looked this clean. The team was very polite and hardworking.", initials: "SB" },
    { name: "Tom H.", location: "Fairfield, CA", text: "Very professional team. They tackled our deep clean with ease. I'm so glad we found them.", initials: "TH" },
    { name: "Jenny W.", location: "Vacaville, CA", text: "Highly recommend their services. They are always on time and do a thorough job every visit.", initials: "JW" },
    { name: "Chris L.", location: "Benicia, CA", text: "Love coming home to a clean house. They are the best cleaning service we've used in Solano County.", initials: "CL" },
    { name: "Amanda M.", location: "Suisun City, CA", text: "Booking was simple, and the cleaning was fantastic. Will definitely use them again.", initials: "AM" },
    { name: "Robert K.", location: "Dixon, CA", text: "Reliable and consistent quality. They always leave our home looking its best.", initials: "RK" }
  ],
  "alameda": [
    { name: "David C.", location: "Oakland, CA", text: "Fantastic cleaning service. They really pay attention to the details. Our Oakland apartment looks brand new.", initials: "DC" },
    { name: "Michelle T.", location: "Fremont, CA", text: "I'm so glad I found them. They make keeping my Fremont home clean so easy and stress-free.", initials: "MT" },
    { name: "Kevin J.", location: "Berkeley, CA", text: "Eco-friendly products and great service. Exactly what we were looking for in a cleaning company.", initials: "KJ" },
    { name: "Lisa M.", location: "Pleasanton, CA", text: "They did a wonderful job on our post-construction clean. Removed every speck of dust.", initials: "LM" },
    { name: "Brian D.", location: "Hayward, CA", text: "Always on time and very thorough. I appreciate their consistency.", initials: "BD" },
    { name: "Karen S.", location: "Livermore, CA", text: "The best cleaning service I've ever used. Highly recommend to anyone in Alameda County.", initials: "KS" }
  ],
  "san-diego": [
    { name: "Amanda K.", location: "San Diego, CA", text: "The best cleaning service in San Diego! So easy to book and the results are always outstanding.", initials: "AK" },
    { name: "Brian F.", location: "Carlsbad, CA", text: "They always do a fantastic job. Our Carlsbad home looks amazing after every visit.", initials: "BF" },
    { name: "Rachel S.", location: "Oceanside, CA", text: "Reliable, professional, and thorough. Highly recommend them to anyone needing a deep clean.", initials: "RS" },
    { name: "John D.", location: "Encinitas, CA", text: "Love their attention to detail. They leave no corner untouched. Very impressed.", initials: "JD" },
    { name: "Melissa P.", location: "Chula Vista, CA", text: "Great service and friendly staff. They always exceed my expectations.", initials: "MP" },
    { name: "Steven W.", location: "Escondido, CA", text: "I've recommended them to all my neighbors. Simply the best.", initials: "SW" }
  ],
  "orange-county": [
    { name: "Jessica W.", location: "Irvine, CA", text: "Amazing service! They keep our Irvine home spotless week after week. Very reliable.", initials: "JW" },
    { name: "Matt P.", location: "Anaheim, CA", text: "Great communication and excellent cleaning. The best service we've found in Orange County.", initials: "MP" },
    { name: "Ashley R.", location: "Newport Beach, CA", text: "Very professional team. They always arrive on time and do a great job. Our house sparkles.", initials: "AR" },
    { name: "Chris B.", location: "Huntington Beach, CA", text: "Highly recommend their deep cleaning service. Our house feels brand new again.", initials: "CB" },
    { name: "Lauren T.", location: "Santa Ana, CA", text: "Consistently excellent service. I love coming home on cleaning day.", initials: "LT" },
    { name: "Michael C.", location: "Costa Mesa, CA", text: "They pay attention to the little details that other cleaners miss. Highly recommended.", initials: "MC" }
  ],
  "santa-clara": [
    { name: "Priya K.", location: "San Jose, CA", text: "Uplevel Cleaning is the best thing to happen to our busy San Jose household. Incredibly thorough and always on time.", initials: "PK" },
    { name: "James T.", location: "Palo Alto, CA", text: "We've tried several services in the Bay Area. Uplevel is head and shoulders above the rest. Spotless every time.", initials: "JT" },
    { name: "Angela M.", location: "Sunnyvale, CA", text: "They make scheduling so easy. The team is professional, friendly, and the results are always outstanding.", initials: "AM" },
    { name: "David L.", location: "Cupertino, CA", text: "My Cupertino home has never looked better. The deep clean was thorough and completely transformed our space.", initials: "DL" },
    { name: "Sandra H.", location: "Mountain View, CA", text: "Reliable, professional, and eco-friendly. Exactly what I was looking for in a cleaning service.", initials: "SH" },
    { name: "Kevin R.", location: "Santa Clara, CA", text: "I love that they use safe, eco-friendly products. Great service and excellent attention to detail.", initials: "KR" }
  ],
  "marin": [
    { name: "Claire B.", location: "San Rafael, CA", text: "Uplevel Cleaning is exceptional! Our Mill Valley home feels fresh and sparkling after every visit.", initials: "CB" },
    { name: "Tom W.", location: "Novato, CA", text: "Very professional team. They always go above and beyond. Best cleaning service in Marin County.", initials: "TW" },
    { name: "Sarah M.", location: "Mill Valley, CA", text: "I love coming home on cleaning day. The attention to detail is remarkable. Highly recommend!", initials: "SM" },
    { name: "Rachel P.", location: "Sausalito, CA", text: "They handled our move-out clean perfectly. Our landlord was so impressed we got the full deposit back.", initials: "RP" },
    { name: "Greg A.", location: "Tiburon, CA", text: "Consistently excellent. I've been using them for six months and they never disappoint.", initials: "GA" },
    { name: "Lisa F.", location: "San Anselmo, CA", text: "The eco-friendly products and professional approach are exactly what we needed. Fantastic service!", initials: "LF" }
  ],
  "san-francisco": [
    { name: "Marcus J.", location: "San Francisco, CA", text: "Finding a great cleaning service in San Francisco is tough. Uplevel Cleaning solved that problem for us!", initials: "MJ" },
    { name: "Hannah L.", location: "Daly City, CA", text: "They are punctual, professional, and incredibly thorough. My apartment has never looked this clean.", initials: "HL" },
    { name: "Aiden R.", location: "South San Francisco, CA", text: "The booking process was seamless and the team was fantastic. Will definitely use them again.", initials: "AR" },
    { name: "Olivia T.", location: "San Mateo, CA", text: "Brilliant service from start to finish. They cleaned our SF apartment spotlessly. Highly recommend.", initials: "OT" },
    { name: "Nathan C.", location: "Burlingame, CA", text: "My go-to cleaning service for our home. Reliable, thorough, and great value for the Bay Area.", initials: "NC" },
    { name: "Fiona M.", location: "Pacifica, CA", text: "They use eco-friendly products which is very important to me. The results are always excellent.", initials: "FM" }
  ],
  "los-angeles": [
    { name: "Brittany H.", location: "Santa Monica, CA", text: "Uplevel Cleaning is the gold standard in LA! My Santa Monica apartment is always immaculate. They're incredibly professional.", initials: "BH" },
    { name: "Carlos R.", location: "Pasadena, CA", text: "We've used many cleaning services in LA. Uplevel is by far the best. Thorough, reliable, and great customer service.", initials: "CR" },
    { name: "Amanda S.", location: "Manhattan Beach, CA", text: "I love how easy the online booking is. The team is always on time and does a phenomenal job.", initials: "AS" },
    { name: "Derek M.", location: "Sherman Oaks, CA", text: "Great experience from booking to the final clean. They didn't miss a single detail in our Valley home.", initials: "DM" },
    { name: "Vanessa L.", location: "Culver City, CA", text: "Highly recommend for anyone in the LA area. They are professional, eco-conscious, and deliver amazing results.", initials: "VL" },
    { name: "Jason P.", location: "Redondo Beach, CA", text: "The deep clean was worth every penny. Our South Bay home feels brand new. Will definitely book again.", initials: "JP" }
  ],
  "phoenix-area": [
    { name: "Jessica M.", location: "Phoenix, AZ", text: "Uplevel Cleaning handles the dust in Phoenix perfectly. Our home is always immaculate and smells amazing.", initials: "JM" },
    { name: "Robert T.", location: "Scottsdale, AZ", text: "Best cleaning service in the Valley! They are always on time and leave our Scottsdale home spotless.", initials: "RT" },
    { name: "Samantha K.", location: "Tempe, AZ", text: "I love their attention to detail. Booking is super easy and the cleaners are very professional.", initials: "SK" },
    { name: "David L.", location: "Mesa, AZ", text: "We've tried several services in the East Valley, but Uplevel is by far the most reliable and thorough.", initials: "DL" },
    { name: "Emily C.", location: "Chandler, AZ", text: "They did an incredible move-out clean for us. Highly recommended for anyone in the Phoenix area.", initials: "EC" },
    { name: "Michael H.", location: "Gilbert, AZ", text: "Transparent pricing and consistent quality. I never have to worry about cleaning day anymore.", initials: "MH" }
  ]
};

const staggerDelays = ["delay-100", "delay-200", "delay-300", "delay-400", "delay-500", "delay-600"];

export const Testimonials = () => {
  const location = useLocation();
  const reviews = REGION_REVIEWS[location.regionSlug] || REGION_REVIEWS["sacramento-area"];
  const { ref: headingRef, isVisible: headingVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.08 });
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const cardWidth = scrollRef.current.children[0]?.clientWidth || 300;

        if (scrollLeft >= maxScroll - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          scrollRef.current.scrollBy({ left: cardWidth + 16, behavior: "smooth" });
        }
      }
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-24 bg-muted/30">
      <div className="container">
        <div
          ref={headingRef}
          className={cn("text-center mb-16 anim-fade-up", headingVisible && "is-visible")}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">What Our Customers Say</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join over 1,500 happy homeowners in the {location.name} who trust us with their homes.
          </p>
        </div>
<div ref={gridRef} className={cn("anim-fade-up", gridVisible && "is-visible")}>
          <div className="md:hidden">
            <div className="grid grid-cols-1 gap-4">
              {reviews.slice(0, 3).map((review, i) => (
                <div
                  key={i}
                  className="bg-background p-5 rounded-2xl shadow-sm border flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border-2 border-primary/20">
                        <AvatarFallback className="bg-primary/5 text-primary font-bold text-xs">
                          {review.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="font-bold text-sm">{review.name}</p>
                          <span className="flex items-center gap-0.5 text-green-600 bg-green-500/10 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Verified
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">{review.location}</p>
                      </div>
                    </div>
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="h-3 w-3 fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed italic text-sm">
                    "{review.text}"
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, i) => (
              <div
                key={i}
                className={cn(
                  "bg-background p-8 rounded-2xl shadow-sm border flex flex-col gap-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 anim-fade-up",
                  staggerDelays[i] || "delay-600",
                  gridVisible && "is-visible"
                )}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 border-2 border-primary/20">
                      <AvatarFallback className="bg-primary/5 text-primary font-bold">
                        {review.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold">{review.name}</p>
                        <span className="flex items-center gap-1 text-green-600 bg-green-500/10 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                          <CheckCircle2 className="h-3 w-3" />
                          Verified
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{review.location}</p>
                    </div>
                  </div>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed italic text-sm">
                  "{review.text}"
                </p>
              </div>
            ))}
          </div>


        </div>
      </div>
    </section>
  );
};
